<script setup lang="ts">
import type { IProps } from './wellbeing-sale--forEmployers.types'
import { useData } from './wellbeing-sale--forEmployers.const'
import WellbeingSaleForEmployersLogo from './components/wellbeing-sale--forEmployers--logo/wellbeing-sale--forEmployers--logo.vue'
import WellbeingSaleForEmployersCard from './components/wellbeing-sale--forEmployers--card/wellbeing-sale--forEmployers--card.vue'
import WellbeingSaleForEmployersCarousel from './components/wellbeing-sale--forEmployers--carousel/wellbeing-sale--forEmployers--carousel.vue'
import { EUI_WITH_ICONS_LAYOUTS } from '@/components/UiListWithIcons/UiListWithIcons.types'

defineProps<IProps>()
const { iconList, sliderData } = useData()
const { isMobile } = useBSBreakpoints()
</script>

<template>
  <div class="forEmployers">
    <div class="forEmployers__header">
      <UiHeader
        :header="$t('wellbeingSale.forEmployers.title')"
        header-size="h5"
        header-size-mobile="h8"
        :position="isMobile ? 'left' :'center'"
      />
    </div>

    <UiListWithIcons
      :layout="isMobile ? EUI_WITH_ICONS_LAYOUTS.COLUMN : EUI_WITH_ICONS_LAYOUTS.ROW"
      :list="iconList()"
    />

    <div class="forEmployers__tool">
      <p
        class="forEmployers__paragraph"
        v-html="$t('wellbeingSale.forEmployers.paragraph1')"
      />

      <div class="forEmployers__tool-columns">
        <p class="forEmployers__tool-column">
          {{ $t('wellbeingSale.forEmployers.column1') }}
        </p>

        <p class="forEmployers__tool-column">
          {{ $t('wellbeingSale.forEmployers.column2') }}
        </p>
      </div>
    </div>

    <WellbeingSaleForEmployersLogo />

    <UiButton
      class="forEmployers__button"
      variant="primary"
      :to="hookTo"
    >
      {{ $t('wellbeingSale.forEmployers.button') }}
    </UiButton>

    <div class="forEmployers__slider">
      <UiHeader
        :header="$t('wellbeingSale.forEmployers.modalTitle')"
        header-size="h6"
        header-size-mobile="h8"
        :position="isMobile ? 'left' :'center'"
      />

      <div
        v-if="!isMobile"
        class="forEmployers__card-container"
      >
        <WellbeingSaleForEmployersCard
          v-for="slide in sliderData"
          :key="slide.title"
          :image="slide.image"
          :title="slide.title"
          :description="slide.description"
          :label="slide.label"
        />
      </div>

      <div v-else>
        <WellbeingSaleForEmployersCarousel
          :slides="sliderData"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.forEmployers {
  position: relative;
  max-width: 136rem;
  width: 100%;
  margin: auto;
  padding: 5rem 1.6rem;
  
  :deep(.purple) {
    color: $purple-400;
  }

  :deep(.icon-list) {
    margin-top: 0;
    margin-bottom: 0;
    padding-bottom: 5.6rem;
  }

  :deep(.icon-list__item_text) {
    @include body-1-medium;
    padding-right: 0;
  }

  &__header {
    padding-bottom: 2.6rem;
  }

  &__paragraph {
    @include h8;
    padding-bottom: 2.4rem;
  }

  &__tool {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0 auto;
  }

  &__tool-columns {
    display: flex;
    flex-direction: column;
    gap: 2.4rem;
  }

  &__tool-column {
    @include body-1-medium;
    max-width: 100%;
    width: 40rem;
  }

  &__button {
    margin: 0 auto 4rem auto;
  }

  &__slider {
    display: flex;
    flex-direction: column;
    gap: 3.2rem;
    max-width: 106.7rem;
    width: 100%;
    margin: 0 auto;
  }
}

@include atM {
  .forEmployers {
    padding: 10rem 0 0 0;

    :deep(.icon-list) {
      padding-bottom: 13.2rem;
    }

    :deep(.button) {
      margin-top: 15rem;
    }

    &__header {
      @include h5;
      margin: auto;
      padding-bottom: 6.4rem;
    }

    &__paragraph {
      @include h6;
      margin: auto;
      padding-bottom: 3.2rem;
    }

    &__tool {
      max-width: 87rem;
    }

    &__tool-columns {
      flex-direction: row;
      gap: 4.8rem;
    }

    &__button {
      margin: 0 auto 8rem auto;
    }

    &__slider {
      gap: 4.8rem;
    }

    &__card-container {
      display: flex;
      flex-wrap: wrap;
      gap: 4rem 1.6rem;
    }
  }
}

</style>
